import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useInactivityTimer } from '../hooks/useInactivityTimer';
import StatusTransitionModal from '../components/StatusTransitionModal';
import Button from '../components/Button';
import ChartSubNav, { CHART_SECTIONS } from '../components/ChartSubNav';
import AllergyBadge from '../components/AllergyBadge';
import AllergyModal from '../components/AllergyModal';
import VisitHistoryModal from '../components/VisitHistoryModal';
import VitalsSection from '../components/VitalsSection';
import MedicationsSection from '../components/MedicationsSection';
import api from '../services/api';
import { StatusBadge } from '../config/patientStatus';
import { IconCircleChevronsDown, IconCircleChevronsUp, IconCalendarWeek, IconHeartbeat, IconCheck, IconChecks, IconExclamationMark, IconFlag2Filled } from '@tabler/icons-react';
import {
  evaluateBP,
  evaluateBMI,
  isBmiFlagRelevant,
  BP_STATUS,
  BMI_STATUS,
  BP_EXPLANATION,
  BP_TOOLTIP,
  BMI_EXPLANATION,
} from '../utils/vitalsIndicators';

// ---------------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------------
function formatDOB(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

function calculateAge(dateStr) {
  if (!dateStr) return null;
  const dob = new Date(dateStr + 'T00:00:00');
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
  return age;
}

function formatLastVisit(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
}

function todayString() {
  return new Date().toISOString().split('T')[0];
}

// ---------------------------------------------------------------------------
// Vitals strip indicator glyphs — same status codes the VitalsSection table
// and legend use, sourced from utils/vitalsIndicators so thresholds never
// drift between the banner and the section.
// ---------------------------------------------------------------------------
const GREEN = 'var(--color-primary)';
const AMBER = 'var(--color-severity-mild)';
const RED = 'var(--color-severity-high)';

function StripBPIndicator({ sbp, dbp }) {
  const status = evaluateBP(sbp, dbp);
  if (status === BP_STATUS.NONE) return null;
  const title = BP_EXPLANATION[status]
    + (BP_TOOLTIP[status] ? ` — ${BP_TOOLTIP[status]}` : '');
  let icon = null;
  if (status === BP_STATUS.CRITICAL) {
    icon = <IconExclamationMark size={13} stroke={2.5} style={{ color: RED }} />;
  } else if (status === BP_STATUS.INTENSIVE) {
    icon = <IconChecks size={13} stroke={2} style={{ color: GREEN }} />;
  } else if (status === BP_STATUS.TARGET) {
    icon = <IconCheck size={13} stroke={2.5} style={{ color: GREEN }} />;
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '2px', flexShrink: 0 }}
      title={title} aria-label={title}>
      {icon}
    </span>
  );
}

function StripBMIIndicator({ bmi, patient }) {
  if (!isBmiFlagRelevant(patient)) return null;
  const status = evaluateBMI(bmi);
  if (status === BMI_STATUS.NONE) return null;
  const color = status === BMI_STATUS.RED ? RED : AMBER;
  const title = BMI_EXPLANATION[status];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '2px', flexShrink: 0 }}
      title={title} aria-label={title}>
      <IconFlag2Filled size={12} style={{ color }} />
    </span>
  );
}

// ---------------------------------------------------------------------------
// SectionPlaceholder
// ---------------------------------------------------------------------------
function SectionPlaceholder({ section }) {
  const Icon = section.icon;
  return (
    <div style={styles.sectionPlaceholder}>
      <Icon size={32} stroke={1.5} style={{ color: 'var(--color-text-muted)', marginBottom: '12px' }} />
      <div style={styles.sectionPlaceholderLabel}>{section.label}</div>
      <div style={styles.sectionPlaceholderStage}>Coming in {section.stage}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PatientChartPage
// ---------------------------------------------------------------------------
export default function PatientChartPage() {
  useInactivityTimer();
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [patient, setPatient] = useState(null);
  const [provider, setProvider] = useState(null);
  const [providers, setProviders] = useState([]);
  const [users, setUsers] = useState([]);
  const [visits, setVisits] = useState([]);
  const [vitals, setVitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeingToday, setSeeingToday] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showAllergyModal, setShowAllergyModal] = useState(false);
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [activeSection, setActiveSection] = useState('labs');
  const [bannerCollapsed, setBannerCollapsed] = useState(false);

  const canEdit = ['provider', 'admin', 'clinical_staff'].includes(user?.role);
  const canChangeStatus = ['provider', 'admin', 'clinical_staff'].includes(user?.role);

  const resolveUser = (userId) => {
    if (!userId) return 'Unknown';
    const u = users.find(u => u.id === userId);
    if (!u) return 'Unknown';
    return `${u.first_name} ${u.last_name}`;
  };

  const loadPatient = async () => {
    try {
      const [patRes, provRes, usersRes, visitsRes, vitalsRes] = await Promise.all([
        api.get(`/patients/${patientId}`),
        api.get('/patients/providers'),
        api.get('/users/directory'),
        api.get(`/patients/${patientId}/visits`),
        api.get(`/patients/${patientId}/vitals`),
      ]);
      setPatient(patRes.data);
      setProviders(provRes.data);
      setUsers(usersRes.data);
      setVisits(visitsRes.data);
      setVitals(vitalsRes.data);
      const prov = provRes.data.find(p => p.id === patRes.data.provider_id);
      setProvider(prov || null);
      api.post(`/dashboard/recently-viewed/${patientId}`).catch(() => { });
    } catch {
      setPatient(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPatient(); }, [patientId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Seen Today ──
  const handleSeenToday = async () => {
    const today = todayString();
    if (visits.some(v => v.visit_date === today)) return;
    setSeeingToday(true);
    try {
      await api.post(`/patients/${patientId}/visits`, {
        visit_date: todayString(),
        is_consult: false,
      });
      const res = await api.get(`/patients/${patientId}/visits`);
      setVisits(res.data);
    } catch {
      // Silently fail — user can open modal for manual entry
    } finally {
      setSeeingToday(false);
    }
  };

  // ── Vitals strip builder ──
  // Carries raw sbp/dbp/bmi so the strip indicators can call the shared
  // evaluators directly, rather than precomputing booleans here (which is how
  // the old bpInGoal/bmiOverLimit drift crept in).
  const buildVitalsStrip = (vitals) => {
    if (!vitals || vitals.length === 0) return null;

    const mostRecent = (field) => vitals.find(v => v[field] != null);

    const bpEntry = vitals.find(v => v.sbp != null && v.dbp != null);
    const hrEntry = mostRecent('hr');
    const bmiEntry = vitals.find(v => v.bmi != null);

    if (!bpEntry && !hrEntry && !bmiEntry) return null;

    const fmt = (dateStr) => {
      if (!dateStr) return '';
      const [y, m, d] = dateStr.split('-');
      return `${parseInt(m)}/${parseInt(d)}/${y.slice(2)}`;
    };

    const dates = [
      bpEntry?.recorded_date,
      hrEntry?.recorded_date,
      bmiEntry?.recorded_date,
    ].filter(Boolean);
    const allSameDate = dates.length > 0 && dates.every(d => d === dates[0]);

    const parts = [];

    if (bpEntry) {
      parts.push({
        kind: 'bp',
        value: `BP ${bpEntry.sbp}/${bpEntry.dbp}`,
        date: allSameDate ? null : `(${fmt(bpEntry.recorded_date)})`,
        sbp: bpEntry.sbp,
        dbp: bpEntry.dbp,
      });
    }

    if (hrEntry) {
      parts.push({
        kind: 'hr',
        value: `HR ${hrEntry.hr}`,
        date: allSameDate ? null : `(${fmt(hrEntry.recorded_date)})`,
      });
    }

    if (bmiEntry) {
      parts.push({
        kind: 'bmi',
        value: `BMI ${parseFloat(bmiEntry.bmi).toFixed(1)}`,
        date: allSameDate ? null : `(${fmt(bmiEntry.recorded_date)})`,
        bmi: bmiEntry.bmi,
      });
    }

    const prefix = allSameDate ? `${fmt(dates[0])}:` : null;

    return { parts, prefix };
  };

  if (loading) return (
    <div style={styles.loading}>Loading patient...</div>
  );
  if (!patient) return (
    <div style={styles.loading}>
      Patient not found.{' '}
      <Button variant="ghost" onClick={() => navigate('/patients')}>Back to registry</Button>
    </div>
  );

  const age = calculateAge(patient.date_of_birth);
  const primaryDx = patient.diagnoses?.find(d => d.diagnosis_role === 'primary');
  const contributing = patient.diagnoses?.filter(d => d.diagnosis_role === 'contributing') || [];
  const allergies = patient.allergies || [];

  const lastVisit = visits.length > 0 ? visits[0] : null;

  const fullName = [
    patient.first_name,
    patient.middle_initial ? `${patient.middle_initial}.` : null,
    patient.last_name,
    patient.suffix || null,
  ].filter(Boolean).join(' ');

  const providerDisplay = provider
    ? `${provider.first_name} ${provider.last_name}${provider.credential ? `, ${provider.credential}` : ''}`
    : null;

  // Line 1: Name | MRN | DOB | Age | Sex | Provider
  const identityItems = [
    patient.mrn ? `MRN: ${patient.mrn}` : null,
    formatDOB(patient.date_of_birth),
    age !== null ? `${age}` : null,
    patient.sex,
    providerDisplay,
  ].filter(Boolean);

  const activeChartSection = CHART_SECTIONS.find(s => s.key === activeSection);
  const vitalsStrip = buildVitalsStrip(vitals);

  const hasDx = !bannerCollapsed && (primaryDx || contributing.length > 0);

  return (
    <div style={styles.chartPage}>

      {/* ── Demographics banner ── */}
      <div style={styles.demoBanner}>

        {/* ── Top section: left content + right buttons ── */}
        <div style={styles.demoMain}>
          <div style={styles.demoLeft}>

            {/* ── Line 1: Identity ── */}
            <div style={styles.nameLine}>
              <span style={styles.patientName}>{fullName}</span>
              {identityItems.map((item, i) => (
                <React.Fragment key={i}>
                  <span style={styles.pipe}>|</span>
                  <span style={styles.infoItem}>{item}</span>
                </React.Fragment>
              ))}
            </div>

            {/* Lines 2 and 3 — hidden when collapsed */}
            {!bannerCollapsed && (
              <>
                {/* ── Line 2: Vitals strip + Visit history ── */}
                <div style={styles.dynamicLine}>

                  {/* Vitals cluster */}
                  {vitalsStrip && (
                    <div
                      style={styles.vitalsStrip}
                      onClick={() => setActiveSection('vitals')}
                      title="Go to Vitals"
                    >
                      <IconHeartbeat
                        size={18}
                        stroke={1.5}
                        style={{ color: 'var(--color-severity-high)', marginRight: '4px', flexShrink: 0 }}
                      />
                      {vitalsStrip.prefix && (
                        <span style={styles.vitalsDate}>
                          {vitalsStrip.prefix}
                        </span>
                      )}
                      {vitalsStrip.parts.map((part, i) => (
                        <span key={i} style={styles.vitalsPart}>
                          {i > 0 && <span style={styles.vitalsDot}>·</span>}
                          <span style={styles.vitalsValue}>{part.value}</span>
                          {part.kind === 'bp' && (
                            <StripBPIndicator sbp={part.sbp} dbp={part.dbp} />
                          )}
                          {part.kind === 'bmi' && (
                            <StripBMIIndicator bmi={part.bmi} patient={patient} />
                          )}
                          {part.date && (
                            <span style={styles.vitalsDate}> {part.date}</span>
                          )}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Visit cluster — calendar icon serves as visual break from vitals */}
                  <div style={styles.visitCluster}>
                    <button
                      style={styles.visitDateBtn}
                      onClick={() => setShowVisitModal(true)}
                      title="View visit history"
                    >
                      <IconCalendarWeek size={18} stroke={1.5} style={{ color: 'var(--color-accent-navy)', flexShrink: 0 }} />
                      <span style={styles.visitDateText}>
                        {lastVisit
                          ? `Last visit ${formatLastVisit(lastVisit.visit_date)}`
                          : 'No visits'
                        }
                      </span>
                    </button>
                    <Button
                      variant="secondary"
                      size="sm"
                      style={{ padding: '3px 8px', fontSize: '12px' }}
                      onClick={handleSeenToday}
                      disabled={seeingToday}
                    >
                      {seeingToday ? 'Saving...' : 'Seen Today'}
                    </Button>
                  </div>
                </div>

                {/* ── Line 3: Status + Allergies ── */}
                <div style={styles.alertLine}>
                  <AllergyBadge
                    nkda={patient.nkda}
                    nka={patient.nka}
                    allergies={allergies}
                    onEdit={() => setShowAllergyModal(true)}
                  />
                  {patient.current_status !== 'active' && (
                    <StatusBadge status={patient.current_status} />
                  )}
                </div>
              </>
            )}
          </div>

          <div style={styles.demoRight}>
            {canEdit && (
              <Button variant="secondary" size="sm" onClick={() => setShowEditModal(true)}>
                Edit
              </Button>
            )}
            {canChangeStatus && (
              <Button variant="secondary" size="sm" onClick={() => setShowStatusModal(true)}>
                Change Status
              </Button>
            )}
            <button
              style={styles.chevronBtn}
              onClick={() => setBannerCollapsed(c => !c)}
              title={bannerCollapsed ? 'Expand demographics' : 'Collapse demographics'}
              aria-label={bannerCollapsed ? 'Expand demographics' : 'Collapse demographics'}
            >
              {bannerCollapsed
                ? <IconCircleChevronsDown size={22} stroke={1} />
                : <IconCircleChevronsUp size={22} stroke={1} />
              }
            </button>
          </div>
        </div>

        {/* ── Diagnoses row — outside demoMain so border spans full width ── */}
        {hasDx && (
          <div style={styles.dxRow}>
            {primaryDx && (
              <div style={styles.dxItem}>
                <span style={styles.dxRoleLabel}>Primary:</span>
                <span style={styles.dxName}>{primaryDx.snomed_code?.display_name}</span>
                <span style={styles.dxIcd}>{primaryDx.snomed_code?.icd10_equivalent}</span>
              </div>
            )}
            {contributing.map((dx, i) => (
              <div key={i} style={styles.dxItem}>
                <span style={{ ...styles.dxRoleLabel, color: 'var(--color-text-muted)' }}>Contributing:</span>
                <span style={styles.dxName}>{dx.snomed_code?.display_name}</span>
                <span style={styles.dxIcd}>{dx.snomed_code?.icd10_equivalent}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Metric badge stub row — Stage 5 ── */}
      <div style={styles.badgeStubRow}>
        <span style={styles.badgeStubText}>
          Metric badges — coming in Stage 5
        </span>
      </div>

      {/* ── Two-column content area ── */}
      <div style={styles.chartBody}>
        <ChartSubNav
          activeSection={activeSection}
          onSelect={setActiveSection}
        />
        <div style={{
          ...styles.chartContent,
          ...(['vitals', 'meds'].includes(activeSection) ? styles.chartContentFull : {}),
        }}>
          {activeSection === 'vitals' ? (
            <VitalsSection
              patient={patient}
              patientId={patientId}
              resolveUser={resolveUser}
              vitals={vitals}
              onVitalsSaved={async () => {
                const res = await api.get(`/patients/${patientId}/vitals`);
                setVitals(res.data);
              }}
            />
          ) : activeSection === 'meds' ? (
            <MedicationsSection
              patient={patient}
              resolveUser={resolveUser}
            />
          ) : (
            activeChartSection && (
              <SectionPlaceholder section={activeChartSection} />
            )
          )}
        </div>
      </div>

      {/* ── Modals ── */}
      {showStatusModal && (
        <StatusTransitionModal
          patient={patient}
          onClose={() => setShowStatusModal(false)}
          onSaved={() => { setShowStatusModal(false); loadPatient(); }}
        />
      )}

      {showAllergyModal && (
        <AllergyModal
          patient={patient}
          resolveUser={resolveUser}
          onClose={() => setShowAllergyModal(false)}
          onSaved={() => loadPatient()}
        />
      )}

      {showVisitModal && (
        <VisitHistoryModal
          patient={patient}
          resolveUser={resolveUser}
          onClose={() => setShowVisitModal(false)}
          onSaved={async () => {
            const res = await api.get(`/patients/${patientId}/visits`);
            setVisits(res.data);
          }}
        />
      )}

      {showEditModal && (
        <EditPatientModalLazy
          patient={patient}
          providers={providers}
          onClose={() => setShowEditModal(false)}
          onSaved={() => { setShowEditModal(false); loadPatient(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// EditPatientModalLazy
// ---------------------------------------------------------------------------
function EditPatientModalLazy({ patient, providers, onClose, onSaved }) {
  const AddEditPatientModal = require('../components/AddEditPatientModal').default;
  return (
    <AddEditPatientModal
      patient={patient}
      providers={providers}
      onClose={onClose}
      onSaved={onSaved}
    />
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const styles = {
  loading: {
    padding: '48px 24px',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-muted)',
    fontFamily: 'var(--font-family-base)',
  },
  chartPage: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  demoBanner: {
    backgroundColor: 'var(--color-bg-card)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    padding: '10px 14px',
    marginBottom: '8px',
    flexShrink: 0,
  },
  demoMain: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
  },
  demoLeft: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
  },
  demoRight: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },

  // ── Line 1: Identity ──
  nameLine: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  patientName: {
    fontSize: 'var(--font-size-xl)',
    fontWeight: 'var(--font-weight-bold)',
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-family-base)',
  },
  pipe: {
    color: 'var(--color-border)',
    fontSize: 'var(--font-size-sm)',
    userSelect: 'none',
  },
  infoItem: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-secondary)',
    fontFamily: 'var(--font-family-base)',
  },

  // ── Line 2: Vitals + Visit ──
  dynamicLine: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap',
  },
  vitalsStrip: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '13px',
    color: 'var(--color-text-secondary)',
    fontFamily: 'var(--font-family-base)',
    cursor: 'pointer',
    padding: '2px 4px',
    borderRadius: 'var(--radius-sm)',
    transition: 'background-color var(--transition-fast)',
    gap: '2px',
  },
  vitalsDate: {
    color: 'var(--color-text-secondary)',
    fontWeight: 'var(--font-weight-medium)',
    marginRight: '4px',
  },
  vitalsPart: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2px',
  },
  vitalsDot: {
    margin: '0 6px',
    color: 'var(--color-text-muted)',
    userSelect: 'none',
    fontSize: 'var(--font-size-lg)',
  },
  vitalsValue: {
    color: 'var(--color-text-secondary)',
    fontWeight: 'var(--font-weight-medium)',
  },
  visitCluster: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  visitDateBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '3px 6px',
    borderRadius: 'var(--radius-sm)',
    transition: 'background-color var(--transition-fast)',
  },
  visitDateText: {
    fontSize: '13px',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-secondary)',
    fontFamily: 'var(--font-family-base)',
  },

  // ── Line 3: Alerts ──
  alertLine: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
    fontSize: 'var(--font-size-sm)',
  },

  // ── Chevron ──
  chevronBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--color-text-secondary)',
    display: 'flex',
    alignItems: 'center',
    padding: '4px',
    borderRadius: 'var(--radius-sm)',
    transition: 'color var(--transition-fast)',
  },

  // ── Diagnoses — sits outside demoMain to span full banner width ──
  dxRow: {
    marginTop: '8px',
    paddingTop: '8px',
    borderTop: '1px solid var(--color-border)',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  dxItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px'
  },
  dxRoleLabel: {
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-primary)',
    minWidth: '90px',
    fontFamily: 'var(--font-family-base)',
  },
  dxName: {
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-family-base)',
  },
  dxIcd: {
    color: 'var(--color-text-muted)',
    fontFamily: 'var(--font-family-base)',
  },

  // ── Badge stub ──
  badgeStubRow: {
    backgroundColor: 'var(--color-bg-card)',
    border: '1px dashed var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    padding: '16px 20px',
    marginBottom: '8px',
    minHeight: '68px',
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  badgeStubText: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
    fontFamily: 'var(--font-family-base)',
    fontStyle: 'italic',
  },

  // ── Chart body ──
  chartBody: {
    display: 'flex',
    flex: 1,
    gap: '0',
    minHeight: 0,
  },
  chartContent: {
    flex: 1,
    marginLeft: '12px',
    backgroundColor: 'var(--color-bg-card)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    minHeight: '400px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  chartContentFull: {
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    padding: '0',
  },
  sectionPlaceholder: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 24px',
    textAlign: 'center',
  },
  sectionPlaceholderLabel: {
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    marginBottom: '6px',
    fontFamily: 'var(--font-family-base)',
  },
  sectionPlaceholderStage: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-muted)',
    fontFamily: 'var(--font-family-base)',
  },
};